// src/pages/PracticePage.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Target, Clock, Zap, ChevronLeft, ChevronRight, Check, X, CheckCircle, RefreshCw, BarChart, Award, Home } from 'lucide-react';
import PartDisplay from '../practice/PartDisplay';
import { logPracticeSession } from '../../utils/activityLogger';
import { auth } from '../../firebase';
import { readSession, writeSession, clearSession } from '../../utils/sessionStore';
import { useLocation } from 'react-router-dom';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

// Mobile-optimized PracticeResults component
const PracticeResults = ({ stats, onBack, onRestart }) => {
  const percentage = stats.totalQuestions > 0 ? (stats.correctAnswers / stats.totalQuestions) * 100 : 0;
  const performanceMessage =
      percentage >= 80 ? { text: 'Ausgezeichnet! 🎉', color: 'text-green-600' } :
      percentage >= 60 ? { text: 'Gut gemacht! 👍', color: 'text-yellow-600' } :
      { text: 'Weiter üben! 💪', color: 'text-red-600' };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 safe-area-inset">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-12">

        {/* Header - Mobile optimized */}
        <div className="text-center mb-6 sm:mb-12">
          <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Award className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">Übung abgeschlossen!</h1>
          <p className={`text-lg sm:text-xl font-semibold ${performanceMessage.color}`}>
            {performanceMessage.text}
          </p>
        </div>

        {/* Stats Cards - Mobile responsive */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-12">
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
              <Target className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{formatTime(stats.timeElapsed)}</div>
            <div className="text-xs sm:text-sm text-gray-600">Zeit</div>
          </div>

          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.bestStreak}</div>
            <div className="text-xs sm:text-sm text-gray-600">Beste Serie</div>
          </div>
        </div>

        {/* Progress Bar - Mobile optimized */}
        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg mb-6 sm:mb-8">
          <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Fortschritt</h3>
          <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4">
            <div
              className="h-3 sm:h-4 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-1000 ease-out"
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-xs sm:text-sm text-gray-600">
            <span>{stats.correctAnswers} von {stats.totalQuestions} richtig</span>
            <span>{Math.round(percentage)}%</span>
          </div>
        </div>

        {/* Action Buttons - Mobile optimized */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={onBack}
            className="flex items-center justify-center px-4 py-3 sm:px-6 sm:py-4 bg-white text-gray-700 font-semibold rounded-lg sm:rounded-xl border-2 border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation min-h-[44px] text-sm sm:text-base"
          >
            <Home className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Zurück zum Dashboard
          </button>

          {onRestart && (
            <button
              onClick={onRestart}
              className="flex items-center justify-center px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold rounded-lg sm:rounded-xl hover:shadow-lg transform hover:-translate-y-1 active:scale-95 transition-all duration-200 touch-manipulation min-h-[44px] text-sm sm:text-base"
            >
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Nochmal üben
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Practice completion handler with activity logging
const handlePracticeComplete = async (practiceResults) => {
  await logPracticeSession({
    sectionId: practiceResults.sectionId,
    sectionName: practiceResults.sectionName || `${practiceResults.sectionId} Practice`,
    timeSpent: Math.floor(practiceResults.duration / 60) || 10,
    correctAnswers: practiceResults.correct,
    totalQuestions: practiceResults.total,
    level: practiceResults.difficulty || 'beginner'
  });
};

const ExamSectionPractice  = ({ examData, onBack }) => {const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sectionId = searchParams.get('sectionId'); // Get sectionId from URL
  const filter = searchParams.get('filter');
  // In-progress answers survive refresh / tab close until results are shown.
  const practiceSessionKey = `logami:v1:sectionPractice:${auth.currentUser?.uid || 'anon'}:${String(examData?.examProvider || '').toLowerCase()}-${String(examData?.level || '').toLowerCase()}:${sectionId || 'none'}:${filter || 'all'}`;
  const restoredRef = useRef();
  if (restoredRef.current === undefined) restoredRef.current = readSession(practiceSessionKey) || null;
  const [allQuestions, setAllQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [sessionStats, setSessionStats] = useState({
    totalQuestions: 0,
    answeredQuestions: 0,
    correctAnswers: 0,
    timeStarted: Date.now(),
    timeElapsed: 0,
    streak: 0,
    bestStreak: 0
  });

  useEffect(() => {
    if (examData && sectionId) {
      const section = examData.sections.find(s => s.sectionId === sectionId);
      if (section) {
        const questions = section.parts.flatMap(part =>
          part.questions ? part.questions.map(q => ({
            ...q,
            partId: part.partId,
            partName: part.partName,
            taskType: part.taskType,
            content: part.content,
            audio: part.audio,
            instructions: part.instructions
          })) : []
        );
        setAllQuestions(questions);
      }
    }
  }, [examData, sectionId]);

  useEffect(() => {
    if (allQuestions.length === 0) return;
    let questionsToDisplay = allQuestions;
    if (filter) {
      questionsToDisplay = allQuestions.filter(q => {
        if (filter === 'reading' && q.taskType.includes('comprehension')) return true;
        if (filter === 'grammar' && (q.taskType.includes('cloze') || q.taskType.includes('reconstruction'))) return true;
        if (filter === 'vocabulary' && q.taskType.includes('cloze')) return true;
        return false;
      });
      if (questionsToDisplay.length === 0) {
        console.warn(`No questions found for filter: "${filter}". Showing all questions for this section.`);
        questionsToDisplay = allQuestions;
      }
    }
    setFilteredQuestions(questionsToDisplay);

    // One-shot restore of a persisted in-progress session (refresh/tab close);
    // the stopwatch resumes paused, time away doesn't count.
    const restored = restoredRef.current;
    restoredRef.current = null;
    if (restored) {
      const elapsed = restored.sessionStats?.timeElapsed ?? 0;
      setSessionStats(prev => ({
        ...prev,
        ...restored.sessionStats,
        totalQuestions: questionsToDisplay.length,
        timeStarted: Date.now() - elapsed * 1000,
        timeElapsed: elapsed,
      }));
      setCurrentQuestionIndex(Math.min(restored.currentQuestionIndex ?? 0, Math.max(0, questionsToDisplay.length - 1)));
      setUserAnswers(restored.userAnswers ?? {});
      setShowResults(false);
      return;
    }

    setSessionStats(prev => ({
      ...prev,
      totalQuestions: questionsToDisplay.length,
      answeredQuestions: 0,
      correctAnswers: 0,
      timeStarted: Date.now(),
      timeElapsed: 0,
      streak: 0
    }));
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setShowResults(false);
  }, [filter, allQuestions]);

  useEffect(() => {
    const timer = setInterval(() => {
      setSessionStats(prev => ({
        ...prev,
        timeElapsed: Math.floor((Date.now() - prev.timeStarted) / 1000)
      }));
    }, 1000);
    return () => clearInterval(timer);
  }, [sessionStats.timeStarted]);

  useEffect(() => {
    if (showResults) return;
    writeSession(practiceSessionKey, {
      userAnswers,
      currentQuestionIndex,
      sessionStats: {
        answeredQuestions: sessionStats.answeredQuestions,
        correctAnswers: sessionStats.correctAnswers,
        streak: sessionStats.streak,
        bestStreak: sessionStats.bestStreak,
        timeElapsed: sessionStats.timeElapsed,
      },
    });
  }, [practiceSessionKey, userAnswers, currentQuestionIndex, sessionStats, showResults]);

  const checkAnswer = (question, userAnswer) => {
    if (!question || userAnswer === undefined) return false;
    return userAnswer === question.correctAnswer;
  };

  const handleAnswerChange = (partId, newAnswers) => {
    const latestAnswerKey = Object.keys(newAnswers).find(key => newAnswers[key] !== userAnswers[key]);
    if (!latestAnswerKey) return;

    const latestAnswerValue = newAnswers[latestAnswerKey];
    const question = filteredQuestions.find(q => q.questionId === latestAnswerKey);

    if (question && userAnswers[latestAnswerKey] === undefined) {
      const isCorrect = checkAnswer(question, latestAnswerValue);
      setSessionStats(prev => ({
        ...prev,
        answeredQuestions: prev.answeredQuestions + 1,
        correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
        streak: isCorrect ? prev.streak + 1 : 0,
        bestStreak: isCorrect && prev.streak + 1 > prev.bestStreak ? prev.streak + 1 : prev.bestStreak
      }));
    }
    setUserAnswers(newAnswers);
  };

  const toggleFlagQuestion = (questionId) => {
    setFlaggedQuestions(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    );
  };

  const handleNext = () => {
    if (currentQuestionIndex < filteredQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      clearSession(practiceSessionKey);
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (filteredQuestions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-4">
          <LogamiLogoSpinner size="xl" className="mx-auto mb-4" decorative={false} alt="Logami loading" />
          <p className="text-sm sm:text-lg font-semibold text-gray-700">
            Lade Übungen für "{filter || 'Alle'}"...
          </p>
        </div>
      </div>
    );
  }

  if (showResults) {
    useEffect(() => {
      const logCompletion = async () => {
        await handlePracticeComplete({
          sectionId: sectionId,
          sectionName: examData?.sections?.find(s => s.sectionId === sectionId)?.sectionName || sectionId,
          duration: sessionStats.timeElapsed,
          correct: sessionStats.correctAnswers,
          total: sessionStats.totalQuestions,
          difficulty: 'intermediate'
        });
      };

      logCompletion();
    }, []);

    return <PracticeResults stats={sessionStats} onBack={onBack} onRestart={() => {
      clearSession(practiceSessionKey);
      setCurrentQuestionIndex(0);
      setUserAnswers({});
      setShowResults(false);
      setSessionStats(prev => ({
        ...prev,
        answeredQuestions: 0,
        correctAnswers: 0,
        timeStarted: Date.now(),
        timeElapsed: 0,
        streak: 0
      }));
    }} />;
  }

  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const mockPartDataForPractice = {
    partId: currentQuestion.partId,
    partName: currentQuestion.partName,
    instructions: currentQuestion.instructions,
    content: currentQuestion.content,
    audio: currentQuestion.audio,
    taskType: currentQuestion.taskType,
    examProvider: examData.examProvider,
    questions: [currentQuestion]
  };

  const accuracy = sessionStats.answeredQuestions > 0 ?
    Math.round((sessionStats.correctAnswers / sessionStats.answeredQuestions) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col safe-area-inset">

      {/* Header - Mobile optimized */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900 active:text-gray-700 font-medium transition-colors touch-manipulation min-h-[44px] min-w-[44px]"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="text-sm sm:text-base">Zurück</span>
            </button>

            {/* Progress Indicator - Mobile optimized */}
            <div className="flex-1 mx-3 sm:mx-6">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">
                  Frage {currentQuestionIndex + 1} von {filteredQuestions.length}
                </span>
                <span className="text-xs sm:text-sm font-medium text-gray-700">
                  {formatTime(sessionStats.timeElapsed)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentQuestionIndex + 1) / filteredQuestions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Stats - Mobile compact */}
            <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
              <div className="text-center">
                <div className="font-bold text-green-600">{accuracy}%</div>
                <div className="text-gray-500 hidden sm:block">Genauigkeit</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-blue-600">{sessionStats.streak}</div>
                <div className="text-gray-500 hidden sm:block">Serie</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile optimized */}
      <main className="flex-1 overflow-y-auto pb-20 sm:pb-24">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
          <PartDisplay
            key={currentQuestion.questionId}
            partData={mockPartDataForPractice}
            savedAnswers={userAnswers}
            onAnswerChange={handleAnswerChange}
            mode="practice"
            flaggedQuestions={flaggedQuestions}
            toggleFlagQuestion={toggleFlagQuestion}
          />
        </div>
      </main>

      {/* Footer Navigation - Mobile optimized */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-slate-200 safe-area-inset-bottom">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-3">
            <button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-3 text-gray-600 hover:text-gray-900 active:text-gray-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors group touch-manipulation min-h-[44px] text-sm sm:text-base"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 group-hover:-translate-x-1 transition-transform" />
              <span className="hidden sm:inline">Vorherige</span>
              <span className="sm:hidden">Zurück</span>
            </button>

            {/* Question Counter - Mobile */}
            <div className="text-center px-2">
              <div className="text-xs text-gray-500">
                {currentQuestionIndex + 1} / {filteredQuestions.length}
              </div>
            </div>

            <button
              onClick={handleNext}
              className="flex items-center justify-center bg-blue-600 text-white px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors group touch-manipulation min-h-[44px] text-sm sm:text-base"
            >
              <span className="hidden sm:inline">
                {currentQuestionIndex === filteredQuestions.length - 1 ? 'Ergebnisse' : 'Nächste'}
              </span>
              <span className="sm:hidden">
                {currentQuestionIndex === filteredQuestions.length - 1 ? 'Fertig' : 'Weiter'}
              </span>
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ExamSectionPractice ;